import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuth, getUserData, getOrgData, reloadCurrentUser, getTrialDaysLeft } from './auth';
 
const AuthContext = createContext(null);
 
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  const [planExpired, setPlanExpired] = useState(false);
  const [loading, setLoading] = useState(true);
 
  const checkExpiry = (org) => {
    if (!org) return;
 
    // Trial expired — was on trial and trialEnds has passed
    if (org.plan === 'trial' && org.trialEnds) {
      const daysLeft = getTrialDaysLeft(org.trialEnds);
      if (daysLeft === 0) {
        setTrialExpired(true);
        return;
      }
    }
 
    // Plan expired — active was explicitly set to false by admin
    if (org.active === false) {
      setPlanExpired(true);
      return;
    }
 
    // All clear
    setTrialExpired(false);
    setPlanExpired(false);
  };
 
  useEffect(() => {
    const unsub = onAuth(async (firebaseUser) => {
      try {
        if (firebaseUser) {
          setUser(firebaseUser);
          setEmailVerified(firebaseUser.emailVerified);

          if (firebaseUser.emailVerified) {
            const data = await getUserData(firebaseUser.uid);
            setUserData(data);
            if (data?.orgId) {
              const org = await getOrgData(data.orgId);
              setOrgData(org);
              checkExpiry(org);
            }
          } else {
            setUserData(null);
            setOrgData(null);
            setTrialExpired(false);
            setPlanExpired(false);
          }
        } else {
          setUser(null);
          setUserData(null);
          setOrgData(null);
          setEmailVerified(false);
          setTrialExpired(false);
          setPlanExpired(false);
        }
      } catch (err) {
        console.error('Failed to load user session:', err);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, []);
 
  const checkVerification = async () => {
    const verified = await reloadCurrentUser();
    if (verified && user) {
      setEmailVerified(true);
      const data = await getUserData(user.uid);
      setUserData(data);
      if (data?.orgId) {
        const org = await getOrgData(data.orgId);
        setOrgData(org);
        checkExpiry(org);
      }
    }
    return verified;
  };
 
  return (
    <AuthContext.Provider value={{
      user, userData, orgData,
      emailVerified, trialExpired, planExpired,
      loading, checkVerification
    }}>
      {children}
    </AuthContext.Provider>
  );
}
 
export const useAuth = () => useContext(AuthContext);